import React, { useEffect, useMemo, useState } from 'react';
import {
  Button,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Tooltip,
  Typography,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { SERVER_NAME } from '../config';
import StoryTypeRow from './StoryTypeRow';

const WINDOW_OPTIONS = ['24h', '7d', '30d'];
const DEFAULT_WINDOW = '7d';
const DEFAULT_ALLOWED_WINDOWS = [...WINDOW_OPTIONS];

const useStyles = makeStyles(() => ({
  root: {
    color: '#ffffff',
    padding: '28px 18px 18px',
    maxWidth: 1480,
    margin: '0 auto',
  },
  hero: {
    padding: 18,
    borderRadius: 16,
    border: '1px solid rgba(77, 121, 255, 0.32)',
    background: 'radial-gradient(circle at top left, rgba(77,121,255,0.26), rgba(9,13,28,0.98) 55%)',
    marginBottom: 18,
  },
  selectorRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    flexWrap: 'wrap',
    marginTop: 14,
  },
  selectorButton: {
    minWidth: 72,
    color: '#dbe4ff',
    borderColor: 'rgba(77,121,255,0.32)',
    background: 'rgba(255,255,255,0.02)',
  },
  selectorActive: {
    background: 'rgba(77,121,255,0.18)',
    color: '#ffffff',
  },
  premiumBadge: {
    marginLeft: 8,
    padding: '2px 6px',
    borderRadius: 999,
    border: '1px solid rgba(255, 196, 0, 0.3)',
    background: 'rgba(255, 196, 0, 0.08)',
    color: '#ffd470',
    fontSize: 10,
    fontWeight: 700,
    letterSpacing: 0.3,
    textTransform: 'uppercase',
  },
  tablePaper: {
    borderRadius: 16,
    border: '1px solid rgba(77,121,255,0.2)',
    background: 'rgba(8, 12, 24, 0.96)',
  },
  headCell: {
    color: '#b9ccff',
    fontSize: 12,
    fontWeight: 700,
    borderBottom: '1px solid rgba(77,121,255,0.18)',
    background: 'rgba(255,255,255,0.02)',
    whiteSpace: 'nowrap',
  },
  bodyCell: {
    borderBottom: '1px solid rgba(255,255,255,0.05)',
  },
  emptyState: {
    padding: '28px 18px',
    color: '#a8b1d5',
  },
}));

function normalizeAllowedWindows(windows, fallback) {
  const normalized = Array.isArray(windows)
    ? windows.map((value) => String(value || '')).filter((value) => WINDOW_OPTIONS.includes(value))
    : [];
  return normalized.length ? normalized : fallback;
}

function highestAllowedWindow(allowedWindows) {
  const normalized = normalizeAllowedWindows(allowedWindows, ['24h']);
  return normalized[normalized.length - 1];
}

function defaultWindowForTier(allowedWindows) {
  const normalized = normalizeAllowedWindows(allowedWindows, ['24h']);
  if (normalized.includes('7d')) return '7d';
  return highestAllowedWindow(normalized);
}

function sortRows(rows, sortKey, sortDirection) {
  const multiplier = sortDirection === 'asc' ? 1 : -1;
  const copy = Array.isArray(rows) ? [...rows] : [];
  copy.sort((a, b) => {
    const aValue = a && a[sortKey] != null ? a[sortKey] : -Infinity;
    const bValue = b && b[sortKey] != null ? b[sortKey] : -Infinity;
    if (aValue === bValue) {
      return String(a && a.display_name ? a.display_name : '').localeCompare(String(b && b.display_name ? b.display_name : ''));
    }
    return (aValue > bValue ? 1 : -1) * multiplier;
  });
  return copy;
}

export default function StoryTypeIndex() {
  const classes = useStyles();
  const [allowedWindows, setAllowedWindows] = useState(DEFAULT_ALLOWED_WINDOWS);
  const [selectedWindow, setSelectedWindow] = useState(DEFAULT_WINDOW);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [sortKey, setSortKey] = useState('count');
  const [sortDirection, setSortDirection] = useState('desc');

  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      const effectiveAllowed = normalizeAllowedWindows(allowedWindows, DEFAULT_ALLOWED_WINDOWS);
      const targetWindow = effectiveAllowed.includes(selectedWindow)
        ? selectedWindow
        : defaultWindowForTier(effectiveAllowed);

      if (targetWindow !== selectedWindow) {
        setSelectedWindow(targetWindow);
        return;
      }

      setLoading(true);
      setError('');

      try {
        const response = await fetch(`${SERVER_NAME}/api/intelligence/story-types?window=${encodeURIComponent(targetWindow)}`, {
          credentials: 'include',
        });

        if (response.status === 403) {
          const payload = await response.json().catch(() => ({}));
          const nextAllowed = normalizeAllowedWindows(payload && payload.allowed_windows, ['24h']);
          if (cancelled) return;
          setAllowedWindows(nextAllowed);
          setSelectedWindow(defaultWindowForTier(nextAllowed));
          setError('');
          setLoading(false);
          return;
        }

        if (!response.ok) {
          throw new Error(`Story types request failed (${response.status})`);
        }

        const payload = await response.json();
        if (cancelled) return;
        setData(payload);
        setError('');
        setLoading(false);
      } catch (err) {
        if (cancelled) return;
        setError(err && err.message ? err.message : 'Failed to load story types.');
        setLoading(false);
      }
    };

    run();

    return () => {
      cancelled = true;
    };
  }, [allowedWindows, selectedWindow]);

  const rows = useMemo(() => {
    const safeRows = Array.isArray(data && data.story_types) ? data.story_types.filter((row) => Number(row && row.count) > 0) : [];
    return sortRows(safeRows, sortKey, sortDirection);
  }, [data, sortDirection, sortKey]);

  const toggleSort = (key) => {
    if (sortKey === key) {
      setSortDirection((current) => (current === 'asc' ? 'desc' : 'asc'));
      return;
    }
    setSortKey(key);
    setSortDirection('desc');
  };

  return (
    <div className={classes.root}>
      <div className={classes.hero}>
        <Typography style={{ color: '#eef2ff', fontSize: 30, fontWeight: 800, lineHeight: 1.05 }}>
          Story Type Index
        </Typography>
        <Typography style={{ color: '#aab6de', marginTop: 8, maxWidth: 860, lineHeight: 1.5 }}>
          Browse the market through E3D&apos;s pattern layer. This index summarizes detected story types, while `/stories` opens the raw stream.
        </Typography>

        <div className={classes.selectorRow}>
          {WINDOW_OPTIONS.map((windowKey) => {
            const disabled = !allowedWindows.includes(windowKey);
            const button = (
              <Button
                key={windowKey}
                variant="outlined"
                disabled={disabled}
                onClick={() => setSelectedWindow(windowKey)}
                className={`${classes.selectorButton} ${selectedWindow === windowKey ? classes.selectorActive : ''}`}
              >
                {windowKey}
                {disabled && <span className={classes.premiumBadge}>Premium</span>}
              </Button>
            );

            if (!disabled) return button;

            return (
              <Tooltip
                key={windowKey}
                title={`${windowKey} window requires premium. Upgrade to E3D Premium for full history.`}
                arrow
              >
                <span>{button}</span>
              </Tooltip>
            );
          })}
        </div>
      </div>

      <Paper className={classes.tablePaper}>
        {loading ? (
          <div className={classes.emptyState} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <CircularProgress size={22} style={{ color: '#7fa8ff' }} />
            <Typography style={{ color: '#cfd8ff' }}>Loading story types…</Typography>
          </div>
        ) : error ? (
          <div className={classes.emptyState}>
            <Typography style={{ color: '#ffb3b3', fontWeight: 600 }}>{error}</Typography>
          </div>
        ) : rows.length === 0 ? (
          <div className={classes.emptyState}>
            <Typography>No stories detected in the selected window. Try expanding to 7d or 30d.</Typography>
          </div>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell className={classes.headCell}>Story Type</TableCell>
                  <TableCell className={classes.headCell} align="right">
                    <TableSortLabel
                      active={sortKey === 'count'}
                      direction={sortKey === 'count' ? sortDirection : 'desc'}
                      onClick={() => toggleSort('count')}
                    >
                      Count
                    </TableSortLabel>
                  </TableCell>
                  <TableCell className={classes.headCell} align="right">
                    <TableSortLabel
                      active={sortKey === 'count_change_pct'}
                      direction={sortKey === 'count_change_pct' ? sortDirection : 'desc'}
                      onClick={() => toggleSort('count_change_pct')}
                    >
                      Change %
                    </TableSortLabel>
                  </TableCell>
                  <TableCell className={classes.headCell} align="right">
                    <TableSortLabel
                      active={sortKey === 'avg_score'}
                      direction={sortKey === 'avg_score' ? sortDirection : 'desc'}
                      onClick={() => toggleSort('avg_score')}
                    >
                      Avg Score
                    </TableSortLabel>
                  </TableCell>
                  <TableCell className={classes.headCell}>Dominant Tokens</TableCell>
                  <TableCell className={classes.headCell} align="right">
                    <TableSortLabel
                      active={sortKey === 'derived_verdict_count'}
                      direction={sortKey === 'derived_verdict_count' ? sortDirection : 'desc'}
                      onClick={() => toggleSort('derived_verdict_count')}
                    >
                      Derived Verdicts
                    </TableSortLabel>
                  </TableCell>
                  <TableCell className={classes.headCell} align="right">
                    <TableSortLabel
                      active={sortKey === 'derived_verdict_win_rate'}
                      direction={sortKey === 'derived_verdict_win_rate' ? sortDirection : 'desc'}
                      onClick={() => toggleSort('derived_verdict_win_rate')}
                    >
                      Derived Win Rate
                    </TableSortLabel>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row) => (
                  <StoryTypeRow key={row.story_type} row={row} />
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>
    </div>
  );
}
